// @LTG-ID: data/journals/asset-sale-vs-stock-sale
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 9 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "asset-sale-vs-stock-sale",
    FRONTMATTER: {
        number: "46",
        section: "Deal Process",
        sectionHref: "/journals#deal-process",
        sectionAnchor: "deal-process",
        title: "Asset Sale vs Stock Sale: What Every Seller Should Understand Before Negotiating",
        deck: "In an asset sale, the buyer purchases the business's individual assets and selected liabilities, leaving the legal entity with the seller.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-03-19",
        publishedHuman: "March 19, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 426,
        timeRequired: "PT3M",
        keywords: "asset sale vs stock sale, asset purchase vs stock purchase, selling a business taxes, business sale structure",
        articleSection: "Deal Process",
        articleBodyPreview: "In an asset sale, the buyer purchases the business's individual assets and selected liabilities, leaving the legal entity with the seller. In a stock sale, the buyer purchases the entity itself, inheriting everything inside it. Buyers generally prefer asset sales for tax and liability reasons; sellers often prefer stock sales. The price gets the headlines, but the structure decides what you keep. Two deals with identical prices can leave very different amounts in the seller's pocket after taxes,",
        complianceNote: "This article is educational and does not constitute tax or legal advice. Deal structure has material tax consequences that depend on your entity type and facts. Consult your CPA and attorney before negotiating.",
        needsDisclaimerFooter: true,
        internalLinks: [
        "how-much-is-my-business-worth",
        "exit-planning-guide",
        "how-long-does-it-take-to-sell-a-business"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Asset Sale vs Stock Sale: What Sellers Should Know · LTG",
        canonical: "https://letstalkgrowth.com/journals/asset-sale-vs-stock-sale",
        ogImage: "/journals/og/asset-sale-vs-stock-sale.jpg",
    },
    BODY_MARKDOWN: `In an asset sale, the buyer purchases the business's individual assets and selected liabilities, leaving the legal entity with the seller. In a stock sale, the buyer purchases the entity itself, inheriting everything inside it. Buyers generally prefer asset sales for tax and liability reasons; sellers often prefer stock sales.

The price gets the headlines, but the structure decides what you keep. Two deals with identical prices can leave very different amounts in the seller's pocket after taxes, which is why structure is negotiated as hard as price.

## The two structures in plain terms

**An asset sale** transfers the pieces: equipment, inventory, customer lists, goodwill, contracts that consent to assignment, and whichever liabilities the buyer explicitly agrees to take. The seller keeps the legal entity, along with anything the buyer did not purchase, including, generally, the entity's past liabilities.

**A stock sale** (or membership interest sale for an LLC) transfers the container. The buyer purchases the ownership interests, and the entity continues uninterrupted with everything inside it: assets, contracts, licenses, history, and liabilities, known and unknown.

## Why buyers usually want assets

Two reasons. Liability: in an asset sale, the buyer can largely leave the entity's history behind. Taxes: purchased assets get a stepped-up basis, which the buyer depreciates or amortizes, creating deductions worth real money. Most small business transactions in the United States are structured as asset sales for exactly these reasons.

## Why sellers often prefer stock

For many sellers, a stock sale can produce more favorable tax character on the gain and a cleaner break from the business's obligations. Sellers of C corporations feel the difference most sharply, because an asset sale by a C corporation can trigger tax at both the corporate and shareholder levels. Entity type, state taxes, and how the price is allocated all change the math, which is why no seller should accept or reject a structure without their CPA modeling both versions.

## The practical middle ground

Real negotiations resolve the tension through price and terms: a buyer who insists on an asset structure may pay more for it, and elections available under the tax code can sometimes give both sides part of what they want. There are also practical tie-breakers: businesses whose value lives in hard-to-transfer contracts or licenses sometimes push toward stock sales simply because reassignment is impractical.

The takeaway is not which structure is better, because it depends. The takeaway is that structure is worth real money, it is negotiable, and the time to understand it is before the letter of intent, not after.`,
    FAQ: [
        { question: "Which is more common, asset sales or stock sales?", answer: "Asset sales dominate small business transactions, primarily because buyers prefer the liability protection and the tax benefits of a stepped-up basis in the purchased assets." },
        { question: "Does deal structure change my taxes as a seller?", answer: "Often significantly. The structure and the allocation of the purchase price determine how much of your gain is taxed at favorable rates versus ordinary rates, and entity type matters greatly. Model both structures with your CPA before negotiating." },
        { question: "Can employees, licenses, and contracts transfer in an asset sale?", answer: "Each must be examined. Many contracts and licenses require consent to assign, and employees are typically rehired by the buyer's entity. This transfer work is a normal but time-consuming part of asset deal closings." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "How Much Is My Business Worth? The Owner's Complete Guide to Business Valuation",
            meta: "5 min · Brief 38",
            to: "/journals/how-much-is-my-business-worth",
        },
        {
            tag: "Exit Planning",
            title: "Exit Planning 101: How to Leave Your Business on Your Own Terms",
            meta: "4 min · Brief 39",
            to: "/journals/exit-planning-guide",
        },
        {
            tag: "Exit Planning",
            title: "How Long Does It Take to Sell a Business?",
            meta: "3 min · Brief 43",
            to: "/journals/how-long-does-it-take-to-sell-a-business",
        },
    ],
};

export default BRIEF;
